/**
 * @module EventBus
 * @since 2.11.0
 */

const eventsKey = Symbol();

/**
* @class EventBus
* @classdesc A simple event bus class, providing subscription and emission functionalities to predefined events.
*/
export class EventBus {
    constructor() {
        this[eventsKey] = new Map();
    }

    /**
    * Registers a callback function to a certain event, i.e. subscribe to an event.
    * @param {*}        event    Event key.
    * @param {Function} callback Callback function to be triggered once the event is fired.
    * @example
    *
    * bus.on('update', () => console.log('Updated!'));
    */
    on(event, callback) {
        let eventSet = this[eventsKey].get(event);

        if (!eventSet) {
            this[eventsKey].set(event, new Set());
            eventSet = this[eventsKey].get(event);
        }

        eventSet.add(callback);
    }

    /**
    * Registers a callback function to a certain event, i.e. subscribe to an event, and execute said function only once.
    * @param {*}        event    Event key.
    * @param {Function} callback Callback function to be triggered once the event is fired.
    * @example
    *
    * bus.once('update', () => console.log('Updated!'));
    */
    once(event, callback) {
        const once = (...args) => {
            this.off(event, once);
            callback(...args);
        };

        this.on(event, once);
    }

    /**
     * Registers a callback for a given event name, unless a callback has already been registered. Makes sure only one callback per event name is registered
     * @param {*} event Event key
     * @param {Function} Callback function to be triggered once the event is fired.
     */
    one(event, callback) {
        if (!this.has(event)) {
            this.on(event, callback);
        }
    }

    /**
     * Does the event identifier any active listeners
     * @param  {*}  event Event key
     * @return {Boolean}
     */
    has(event) {
        return this[eventsKey].has(event);
    }

    /**
    * Unregisters a callback function from a certain event, i.e. unsubscribe to an event.
    * @param {*}        event      Event key.
    * @param {Function} [callback] Callback function to deregister. If not passed all listeners will be removed.
    * @example
    *
    * bus.off('update');
    * bus.off('update', updatedCallback);
    */
    off(event, callback) {
        if (!this[eventsKey].has(event)) {
            return;
        }

        if (typeof callback === 'undefined') {
            this[eventsKey].delete(event);
            return;
        }

        const eventSet = this[eventsKey].get(event);
        eventSet.delete(callback);

        if (eventSet.size === 0) {
            this[eventsKey].delete(event);
        }
    }

    /**
    * "Fire" a certain event, triggering all callback functions for specified event with the provided data.
    * @param {*}  event  Event key you wish to emit.
    * @param {*[]}     [args] Callback arguments to pass to the event's list of callback functions.
    * @example
    *
    * bus.emit('update', 'Updated');
    */
    emit(event, ...args) {
        const eventKey = this[eventsKey].get(event);
        eventKey && eventKey.forEach((callback) => {
            callback(...args);
        });
    }

    /**
    * "Fire" an event in a deferred fashion, i.e. in a separate call-stack from the current execution block.
    * @param {*}   event  Event key you wish to emit.
    * @param {*[]} [...args] Callback arguments to pass to the event's list of callback functions.
    * @return {Promise} A promise that resolves once the the event was emitted.
    * @example
    *
    * bus.deferred('update', 'Updated');
    */
    deferred(event, ...args) {
        return Promise.resolve().then(() => {
            this.emit(event, ...args);
        });
    }
}
